import React, { useMemo } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useFarm, farmInitials } from "@/lib/farmContext";
import { TEMPLATES, MENU } from "@/lib/actionTemplates";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapPin } from "lucide-react";
import { cn } from "@/lib/utils";

export default function Actions() {
  const { user } = useAuth();
  const { farms, activeFarm, activeFarmId, setActiveFarmId } = useFarm();
  const isHandler = user?.role === "handler";
  const today = useMemo(() => new Date().toLocaleDateString("es-AR", { day: "2-digit", month: "long", year: "numeric" }), []);
  const noFarms = farms.length === 0;
  const needsFarm = noFarms || !activeFarmId;

  // El veterinario tiene acceso acotado a una sola plantilla — se salta el
  // hub y va directo a la palpación (P11).
  if (user?.role === "vet") return <Navigate to="/acciones/11" replace />;

  return (
    <div className="max-w-2xl mx-auto" data-testid="actions-page">
      <div className="mb-6">
        <div className="text-[10.5px] font-mono uppercase tracking-[0.14em] text-muted-foreground">{today}</div>
        <h1 className="font-editorial text-[34px] sm:text-[42px] leading-[1.05] tracking-tight mt-1">Acciones</h1>
        <p className="text-[14px] text-muted-foreground mt-2">
          Hola, <span className="italic">{user?.name?.split(" ")[0] || ""}</span>. Primero elegí la finca, después la acción.
        </p>
      </div>

      {/* Selector de finca activa (global) — la acción se ejecuta en esta finca */}
      <div className="mb-7 rounded-[var(--radius)] border-2 border-border bg-card p-4 shadow-paper" data-testid="active-farm-card">
        <div className="text-[10.5px] font-mono uppercase tracking-[0.14em] text-muted-foreground mb-2.5">Finca / lugar de la acción</div>
        {noFarms ? (
          <Link to="/fincas" className="flex items-center gap-2.5 text-[14px] font-medium text-primary" data-testid="active-farm-empty">
            <MapPin size={18} /> Creá una finca primero
          </Link>
        ) : (
          <div className="flex items-center gap-3">
            <span className="grid place-items-center h-12 w-12 rounded-[calc(var(--radius)-0.3rem)] bg-primary/10 border-2 border-primary/30 font-heading font-bold text-[15px] text-primary shrink-0" data-testid="active-farm-initials">
              {activeFarm ? farmInitials(activeFarm.name) : <MapPin size={18} />}
            </span>
            {farms.length === 1 ? (
              <div className="min-w-0">
                <div className="font-heading font-bold text-[18px] leading-tight truncate" data-testid="active-farm-name">{activeFarm?.name}</div>
                <div className="text-[12px] text-muted-foreground">Finca activa</div>
              </div>
            ) : (
              <Select value={activeFarmId || ""} onValueChange={setActiveFarmId}>
                <SelectTrigger className="h-12 flex-1 text-[15px] font-medium" data-testid="active-farm-select">
                  <SelectValue placeholder="Elegí la finca…" />
                </SelectTrigger>
                <SelectContent>
                  {farms.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
        )}
        {needsFarm && !noFarms && (
          <p className="mt-3 text-[12.5px] font-medium text-[hsl(var(--state-missing))] flex items-center gap-1.5" data-testid="active-farm-warning">
            Elegí una finca para registrar acciones.
          </p>
        )}
      </div>

      <div className={cn("flex flex-col gap-9 transition-opacity", needsFarm && "opacity-40 pointer-events-none select-none")} aria-disabled={needsFarm}>
        {MENU.map((group) => {
          const items = group.items
            .map((id) => TEMPLATES[id])
            .filter((t) => t && !(t.ownerOnly && isHandler));
          if (items.length === 0) return null;
          return (
            <section key={group.key} data-testid={`actions-group-${group.key}`}>
              <div className="flex items-center gap-2.5 mb-3.5 pb-2.5 border-b border-border">
                <span className="h-5 w-[6px] rounded-full" style={{ background: group.color }} />
                <h2 className="font-heading font-bold text-[16px] tracking-tight text-foreground">{group.label}</h2>
                <span className="ml-auto text-[10.5px] font-mono text-muted-foreground">
                  {items.length}{group.note ? ` · ${group.note}` : ""}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {items.map((t) => <ActionButton key={t.id} t={t} />)}
              </div>
            </section>
          );
        })}
      </div>

      <div className="mt-9 rounded-[var(--radius)] border border-border bg-muted/30 px-4 py-3.5 text-[12px] text-muted-foreground leading-relaxed" data-testid="actions-legend">
        <span className="font-heading font-semibold text-foreground">Códigos de respuesta:</span>{" "}
        <span role="img" aria-label="sí">✅</span> Sí ·{" "}
        <span role="img" aria-label="no">⛔️</span> No ·{" "}
        <span role="img" aria-label="bien">🟢</span> Bien ·{" "}
        <span role="img" aria-label="no coincide">🔴</span> No coincide ·{" "}
        <span role="img" aria-label="vacía">🟣</span> Vacía ·{" "}
        <span role="img" aria-label="amarillo">🟨</span> Amarillo ·{" "}
        <span role="img" aria-label="gris">⬜️</span> Gris
      </div>
    </div>
  );
}

function ActionButton({ t }) {
  const color = `var(--c-${t.cat})`;
  const to = t.navTo || `/acciones/${t.id}`;
  return (
    <Link
      to={to}
      data-testid={`action-btn-${t.id}`}
      style={{ "--cat": color }}
      className={cn(
        "group relative flex items-center gap-4 rounded-[var(--radius)] border-2 border-border bg-card p-3.5 min-h-[76px]",
        "shadow-paper transition-[transform,border-color] duration-150 active:translate-y-[2px] hover:border-[hsl(var(--cat))]"
      )}
    >
      <span
        className="inline-flex h-14 w-auto min-w-[56px] px-2 rounded-[calc(var(--radius)-0.3rem)] items-center justify-center text-[22px] leading-none whitespace-nowrap shrink-0"
        style={{ background: `hsl(${color} / 0.14)`, border: `2px solid hsl(${color} / 0.4)` }}
      >
        <span role="img" aria-label={t.title}>{t.ico}</span>
      </span>
      <span className="flex flex-col min-w-0 flex-1">
        <span className="font-heading font-bold text-[15.5px] text-foreground leading-tight">{t.title}</span>
        {t.sub ? <span className="text-[12.5px] text-muted-foreground mt-0.5">{t.sub}</span> : null}
      </span>
      <span className="font-mono text-[10px] font-bold text-muted-foreground/60 bg-muted/60 rounded px-1.5 py-0.5 shrink-0">P{t.id}</span>
    </Link>
  );
}
